import { useEffect, useState } from 'react';
import {
    ServiceBooking,
    Reservation,
    ReservationPaxGroup,
    ServiceBookingPaxGroup,
    ServiceBookingPerson,
    Person,
} from '@turizi/shared';
import {
    useReservation,
    useServiceBooking,
    useReservationPaxGroups,
    useAssignPaxGroupToServiceBooking,
    useRemovePaxGroupFromServiceBooking,
    useUpdateServiceBooking,
} from '@/hooks/api-hooks';
import { Loader, ActionIcon, Checkbox, NumberInput, Button, Tooltip, TextInput } from '@mantine/core';
import {
    IconLinkPlus,
    IconUnlink,
    IconUsers,
    IconUser,
    IconRefresh,
    IconRotateClockwise,
    IconCheck,
    IconUsersGroup,
    IconDoorExit,
    IconDoorEnter,
    IconHandClick,
    IconSearch,
    IconX,
    IconArrowsExchange,
    IconAdjustmentsHorizontal,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PaxCategoryBadge, ShowPaxGroup } from '../snippets';

interface BookingPaxsAssociationProps {
    booking: ServiceBooking;
    reservation?: Reservation;
}

type SizeKey = 'adults' | 'children' | 'seniors' | 'infants' | 'guests';

const SIZE_KEYS: SizeKey[] = ['adults', 'children', 'seniors', 'infants', 'guests'];

interface PersonDraftState {
    selected: boolean;
    adults: number;
    children: number;
    seniors: number;
    infants: number;
    guests: number;
}

interface GroupDraftState {
    adults: number;
    children: number;
    seniors: number;
    infants: number;
    guests: number;
    persons: Record<number, PersonDraftState>;
}

function getPersonDefaultSizes(person: Person): Record<SizeKey, number> {
    const adults = Number(person.adults) || 0;
    const children = Number(person.children) || 0;
    const seniors = Number(person.seniors) || 0;
    const infants = Number(person.infants) || 0;
    const guests = Number(person.guests) || 0;

    return { adults, children, seniors, infants, guests };
}

function getPersonEffectiveDeltaSizes(person: Person, state?: PersonDraftState): Record<SizeKey, number> {
    return state
        ? {
            adults: Number(state.adults) || 0,
            children: Number(state.children) || 0,
            seniors: Number(state.seniors) || 0,
            infants: Number(state.infants) || 0,
            guests: Number(state.guests) || 0,
        }
        : getPersonDefaultSizes(person);
}

function buildGroupDraft(
    group: ReservationPaxGroup,
    linkedGroup?: ServiceBookingPaxGroup
): GroupDraftState {
    const adults = linkedGroup ? (Number(linkedGroup.adults ?? group.adults) || 0) : (Number(group.adults) || 0);
    const children = linkedGroup ? (Number(linkedGroup.children ?? group.children) || 0) : (Number(group.children) || 0);
    const seniors = linkedGroup ? (Number(linkedGroup.seniors ?? group.seniors) || 0) : (Number(group.seniors) || 0);
    const infants = linkedGroup ? (Number(linkedGroup.infants ?? group.infants) || 0) : (Number(group.infants) || 0);
    const guests = linkedGroup ? (Number(linkedGroup.guests ?? group.guests) || 0) : (Number(group.guests) || 0);

    const hasExplicitBookingPersons = Boolean(
        linkedGroup &&
        Array.isArray(linkedGroup.booking_persons) &&
        (linkedGroup.booking_persons.length > 0 || linkedGroup.is_custom_config)
    );

    const bpMap = new Map<number, ServiceBookingPerson>();
    if (linkedGroup?.booking_persons) {
        for (const bp of linkedGroup.booking_persons) {
            if (bp && bp.person_id) {
                bpMap.set(Number(bp.person_id), bp);
            }
        }
    }

    const persons: Record<number, PersonDraftState> = {};
    for (const p of group.persons || []) {
        if (!p.id) continue;
        const pid = Number(p.id);
        const bp = bpMap.get(pid);
        const isSelected = linkedGroup
            ? (hasExplicitBookingPersons ? Boolean(bp) : true)
            : false;

        persons[pid] = {
            selected: isSelected,
            adults: bp ? (Number(bp.adults) || 0) : (Number(p.adults) || 0),
            children: bp ? (Number(bp.children) || 0) : (Number(p.children) || 0),
            seniors: bp ? (Number(bp.seniors) || 0) : (Number(p.seniors) || 0),
            infants: bp ? (Number(bp.infants) || 0) : (Number(p.infants) || 0),
            guests: bp ? (Number(bp.guests) || 0) : (Number(p.guests) || 0),
        };
    }

    return {
        adults,
        children,
        seniors,
        infants,
        guests,
        persons,
    };
}

export default function BookingPaxsAssociation({
    booking,
    reservation,
}: BookingPaxsAssociationProps) {
    const { t } = useTranslation();
    const reservationId = booking.reservation_id || reservation?.id;

    const { data: resData, isLoading } = useReservation(reservationId || 0);
    const { data: bookingRes } = useServiceBooking(booking.id);
    const { data: resPaxGroups = [] } = useReservationPaxGroups(reservationId || 0);
    const assignGroupMutation = useAssignPaxGroupToServiceBooking();
    const removeGroupMutation = useRemovePaxGroupFromServiceBooking();

    const [actioningGroupId, setActioningGroupId] = useState<number | null>(null);
    const [groupDrafts, setGroupDrafts] = useState<Record<number, GroupDraftState>>({});
    const [dirtyGroupIds, setDirtyGroupIds] = useState<Set<number>>(new Set());
    const [groupSearchQueries, setGroupSearchQueries] = useState<Record<number, string>>({});
    const [expandedPersonSizeIds, setExpandedPersonSizeIds] = useState<Record<number, boolean>>({});

    const activeReservation = resData?.data || reservation;
    const activeBooking = bookingRes?.data || booking;
    const bookingGroups: ServiceBookingPaxGroup[] = activeBooking?.pax_groups || booking?.pax_groups || [];

    useEffect(() => {
        setGroupDrafts((prev) => {
            const next: Record<number, GroupDraftState> = { ...prev };
            for (const g of resPaxGroups) {
                if (!g.id) continue;
                if (dirtyGroupIds.has(g.id) && next[g.id]) continue;
                const linked = bookingGroups.find(
                    (bg) => (bg.pax_group_id || bg.pax_group?.id) === g.id
                );
                next[g.id] = buildGroupDraft(g, linked);
            }
            return next;
        });
    }, [resPaxGroups, bookingGroups, dirtyGroupIds]);

    if (!reservationId) {
        return (
            <div className="p-3 text-center text-xs text-gray-500 font-medium">
                {t('no_reservation_associated_with_booking')}
            </div>
        );
    }

    if (isLoading && !activeReservation) {
        return (
            <div className="flex items-center justify-center p-6 gap-2">
                <Loader size="xs" color="orange" />
                <span className="text-xs text-gray-500 font-medium">{t('loading_passengers')}</span>
            </div>
        );
    }

    if (!activeReservation) {
        return (
            <div className="p-3 text-center text-xs text-red-500 font-medium">
                {t('failed_to_load_reservation')}
            </div>
        );
    }

    const hasServiceOrder =
        (activeBooking?.service_order_bookings?.length || booking?.service_order_bookings?.length || 0) > 0;

    const confirmIfServiceOrder = () => {
        if (hasServiceOrder && !window.confirm(t('confirm_modify_booking_with_service_order'))) {
            return false;
        }
        return true;
    };

    const buildBookingPersonsPayload = (
        group: ReservationPaxGroup,
        draft: GroupDraftState
    ): Partial<ServiceBookingPerson>[] => {
        const result: Partial<ServiceBookingPerson>[] = [];
        for (const p of group.persons || []) {
            if (!p.id) continue;
            const pid = Number(p.id);
            const pState = draft.persons[pid];
            if (!pState || !pState.selected) continue;

            const isPersonCustom =
                pState.adults !== (Number(p.adults) || 0) ||
                pState.children !== (Number(p.children) || 0) ||
                pState.seniors !== (Number(p.seniors) || 0) ||
                pState.infants !== (Number(p.infants) || 0) ||
                pState.guests !== (Number(p.guests) || 0);

            result.push({
                person_id: pid,
                adults: pState.adults,
                children: pState.children,
                seniors: pState.seniors,
                infants: pState.infants,
                guests: pState.guests,
                is_custom_config: isPersonCustom,
            });
        }
        return result;
    };

    const saveGroupDraft = async (group: ReservationPaxGroup, draftToSave: GroupDraftState) => {
        if (!group.id) return;
        if (!confirmIfServiceOrder()) return;

        const bookingPersonsPayload = buildBookingPersonsPayload(group, draftToSave);
        const totalGroupPersons = (group.persons || []).filter((p) => p.id).length;

        const isGroupCustom =
            draftToSave.adults !== (Number(group.adults) || 0) ||
            draftToSave.children !== (Number(group.children) || 0) ||
            draftToSave.seniors !== (Number(group.seniors) || 0) ||
            draftToSave.infants !== (Number(group.infants) || 0) ||
            draftToSave.guests !== (Number(group.guests) || 0) ||
            bookingPersonsPayload.length !== totalGroupPersons ||
            bookingPersonsPayload.some((bp) => Boolean(bp.is_custom_config));

        setActioningGroupId(group.id);
        try {
            await assignGroupMutation.mutateAsync({
                serviceBookingId: booking.id,
                groupId: group.id,
                reservationId,
                data: {
                    adults: draftToSave.adults,
                    children: draftToSave.children,
                    seniors: draftToSave.seniors,
                    infants: draftToSave.infants,
                    guests: draftToSave.guests,
                    is_custom_config: isGroupCustom,
                    booking_persons: bookingPersonsPayload as ServiceBookingPerson[],
                },
            });
            setDirtyGroupIds((prev) => {
                const next = new Set(prev);
                next.delete(group.id!);
                return next;
            });
        } finally {
            setActioningGroupId(null);
        }
    };

    const handleAssignGroup = async (group: ReservationPaxGroup, resetToDefaults = false) => {
        if (!group.id) return;
        if (!confirmIfServiceOrder()) return;

        setActioningGroupId(group.id);
        try {
            const defaultBookingPersons: Partial<ServiceBookingPerson>[] = (group.persons || [])
                .filter((p): p is Person & { id: number } => Boolean(p.id))
                .map((p) => ({
                    person_id: Number(p.id),
                    adults: Number(p.adults) || 0,
                    children: Number(p.children) || 0,
                    seniors: Number(p.seniors) || 0,
                    infants: Number(p.infants) || 0,
                    guests: Number(p.guests) || 0,
                    is_custom_config: false,
                }));

            await assignGroupMutation.mutateAsync({
                serviceBookingId: booking.id,
                groupId: group.id,
                reservationId,
                data: resetToDefaults
                    ? {
                        adults: Number(group.adults) || 0,
                        children: Number(group.children) || 0,
                        seniors: Number(group.seniors) || 0,
                        infants: Number(group.infants) || 0,
                        guests: Number(group.guests) || 0,
                        is_custom_config: false,
                        booking_persons: defaultBookingPersons as ServiceBookingPerson[],
                    }
                    : undefined,
            });
            setDirtyGroupIds((prev) => {
                const next = new Set(prev);
                next.delete(group.id!);
                return next;
            });
        } finally {
            setActioningGroupId(null);
        }
    };

    const handleRemoveGroup = async (groupId: number) => {
        if (!confirmIfServiceOrder()) return;
        setActioningGroupId(groupId);
        try {
            await removeGroupMutation.mutateAsync({
                serviceBookingId: booking.id,
                groupId,
                reservationId,
            });
            setDirtyGroupIds((prev) => {
                const next = new Set(prev);
                next.delete(groupId);
                return next;
            });
        } finally {
            setActioningGroupId(null);
        }
    };

    const handleTogglePerson = async (
        group: ReservationPaxGroup,
        person: Person,
        checked: boolean,
        isGroupCurrentlyLinked: boolean
    ) => {
        if (!group.id || !person.id) return;
        const gid = group.id;
        const pid = Number(person.id);

        const linked = bookingGroups.find(
            (bg) => (bg.pax_group_id || bg.pax_group?.id) === gid
        );
        const currentDraft = groupDrafts[gid] || buildGroupDraft(group, linked);
        const currentPersonDraft = currentDraft.persons[pid] || {
            selected: false,
            ...getPersonDefaultSizes(person),
        };

        const nextPersons: Record<number, PersonDraftState> = {
            ...currentDraft.persons,
            [pid]: {
                ...currentPersonDraft,
                selected: checked,
            },
        };

        const personDelta = getPersonEffectiveDeltaSizes(person, currentPersonDraft);
        const hasPersonExplicitSizes =
            (personDelta.adults + personDelta.children + personDelta.seniors + personDelta.infants + personDelta.guests) > 0;

        let nextAdults = currentDraft.adults;
        let nextChildren = currentDraft.children;
        let nextSeniors = currentDraft.seniors;
        let nextInfants = currentDraft.infants;
        let nextGuests = currentDraft.guests;

        if (!isGroupCurrentlyLinked && checked) {
            if (hasPersonExplicitSizes) {
                nextAdults = personDelta.adults;
                nextChildren = personDelta.children;
                nextSeniors = personDelta.seniors;
                nextInfants = personDelta.infants;
                nextGuests = personDelta.guests;
            } else {
                nextAdults = Number(group.adults) || 0;
                nextChildren = Number(group.children) || 0;
                nextSeniors = Number(group.seniors) || 0;
                nextInfants = Number(group.infants) || 0;
                nextGuests = Number(group.guests) || 0;
            }
        } else if (isGroupCurrentlyLinked && hasPersonExplicitSizes) {
            const sign = checked ? 1 : -1;
            nextAdults = Math.max(0, currentDraft.adults + sign * personDelta.adults);
            nextChildren = Math.max(0, currentDraft.children + sign * personDelta.children);
            nextSeniors = Math.max(0, currentDraft.seniors + sign * personDelta.seniors);
            nextInfants = Math.max(0, currentDraft.infants + sign * personDelta.infants);
            nextGuests = Math.max(0, currentDraft.guests + sign * personDelta.guests);
        }

        const nextDraft: GroupDraftState = {
            adults: nextAdults,
            children: nextChildren,
            seniors: nextSeniors,
            infants: nextInfants,
            guests: nextGuests,
            persons: nextPersons,
        };

        setGroupDrafts((prev) => ({ ...prev, [gid]: nextDraft }));
        await saveGroupDraft(group, nextDraft);
    };

    const handleGroupSizeChange = (group: ReservationPaxGroup, key: SizeKey, val: number) => {
        if (!group.id) return;
        const gid = group.id;
        const linked = bookingGroups.find((bg) => (bg.pax_group_id || bg.pax_group?.id) === gid);
        const currentDraft = groupDrafts[gid] || buildGroupDraft(group, linked);
        const nextVal = Math.max(0, Number(val) || 0);

        setGroupDrafts((prev) => ({
            ...prev,
            [gid]: {
                ...currentDraft,
                [key]: nextVal,
            },
        }));
        setDirtyGroupIds((prev) => new Set(prev).add(gid));
    };

    const handlePersonSizeChange = (
        group: ReservationPaxGroup,
        person: Person,
        key: SizeKey,
        val: number
    ) => {
        if (!group.id || !person.id) return;
        const gid = group.id;
        const pid = Number(person.id);
        const linked = bookingGroups.find((bg) => (bg.pax_group_id || bg.pax_group?.id) === gid);
        const currentDraft = groupDrafts[gid] || buildGroupDraft(group, linked);
        const currentPersonDraft = currentDraft.persons[pid] || {
            selected: true,
            ...getPersonDefaultSizes(person),
        };

        const nextVal = Math.max(0, Number(val) || 0);
        const delta = nextVal - (Number(currentPersonDraft[key]) || 0);

        setGroupDrafts((prev) => ({
            ...prev,
            [gid]: {
                ...currentDraft,
                [key]: currentPersonDraft.selected
                    ? Math.max(0, (Number(currentDraft[key]) || 0) + delta)
                    : currentDraft[key],
                persons: {
                    ...currentDraft.persons,
                    [pid]: {
                        ...currentPersonDraft,
                        [key]: nextVal,
                    },
                },
            },
        }));
        setDirtyGroupIds((prev) => new Set(prev).add(gid));
    };

    const handleResetGroupDraft = (group: ReservationPaxGroup) => {
        if (!group.id) return;
        const gid = group.id;
        const linked = bookingGroups.find((bg) => (bg.pax_group_id || bg.pax_group?.id) === gid);
        setGroupDrafts((prev) => ({
            ...prev,
            [gid]: buildGroupDraft(group, linked),
        }));
        setDirtyGroupIds((prev) => {
            const next = new Set(prev);
            next.delete(gid);
            return next;
        });
    };

    const handleMatchBookingCapacity = async (group: ReservationPaxGroup) => {
        if (!group.id) return;
        const gid = group.id;
        const linked = bookingGroups.find((bg) => (bg.pax_group_id || bg.pax_group?.id) === gid);
        const currentDraft = groupDrafts[gid] || buildGroupDraft(group, linked);
        const nextDraft: GroupDraftState = {
            ...currentDraft,
            adults: Number(activeBooking.adults) || 0,
            children: Number(activeBooking.children) || 0,
            seniors: Number(activeBooking.seniors) || 0,
            infants: Number(activeBooking.infants) || 0,
            guests: Number(activeBooking.guests) || 0,
        };
        setGroupDrafts((prev) => ({ ...prev, [gid]: nextDraft }));
        await saveGroupDraft(group, nextDraft);
    };

    const handleSelectAllPersons = async (group: ReservationPaxGroup, selectAll: boolean) => {
        if (!group.id) return;
        const gid = group.id;
        const linked = bookingGroups.find((bg) => (bg.pax_group_id || bg.pax_group?.id) === gid);
        const currentDraft = groupDrafts[gid] || buildGroupDraft(group, linked);
        const nextPersons: Record<number, PersonDraftState> = { ...currentDraft.persons };

        for (const p of group.persons || []) {
            if (!p.id) continue;
            const pid = Number(p.id);
            const curP = nextPersons[pid] || {
                selected: false,
                ...getPersonDefaultSizes(p),
            };
            nextPersons[pid] = {
                ...curP,
                selected: selectAll,
            };
        }

        const nextDraft: GroupDraftState = {
            ...currentDraft,
            persons: nextPersons,
        };
        setGroupDrafts((prev) => ({ ...prev, [gid]: nextDraft }));
        await saveGroupDraft(group, nextDraft);
    };

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            className="flex flex-col gap-3 min-w-[340px] max-w-[440px] max-h-[80vh] overflow-y-auto"
        >
            {
                <div>
                    <div className="space-y-2">
                        {[...resPaxGroups]
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((group) => {
                            const gid = group.id || 0;
                            const linkedGroup = bookingGroups.find(
                                (bg) => (bg.pax_group_id || bg.pax_group?.id) === gid
                            );
                            const isLinked = Boolean(linkedGroup);
                            const draft = groupDrafts[gid] || buildGroupDraft(group, linkedGroup);
                            const isDirty = dirtyGroupIds.has(gid);
                            const isLoadingThis =
                                actioningGroupId === gid &&
                                (assignGroupMutation.isPending || removeGroupMutation.isPending);

                            const groupPersons = [...(group.persons || [])].sort((a, b) => {
                                const catA = a.category || '';
                                const catB = b.category || '';
                                if (catA !== catB) return catA.localeCompare(catB);
                                return (a.name || '').localeCompare(b.name || '');
                            });

                            const searchQ = (groupSearchQueries[gid] || '').trim().toLowerCase();
                            const visibleGroupPersons = searchQ
                                ? groupPersons.filter((p) =>
                                    (p.name || '').toLowerCase().includes(searchQ) ||
                                    (p.document_number || '').toLowerCase().includes(searchQ)
                                )
                                : groupPersons;

                            return (
                                <div
                                    key={gid}
                                    className={`
                                        p-1.5 rounded-lg 
                                        border-2 
                                        text-xs transition-all space-y-2 
                                        bg-white
                                        ${isLinked
                                            ? 'border-green-200'
                                            : 'border-slate-200/80'
                                        }`}
                                >
                                    {/* Group Header Row */}
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
                                            <div
                                                className="w-5 h-5 rounded-md 
                                                    flex items-center justify-center 
                                                    shrink-0 
                                                    text-[10px] font-bold">
                                                <IconUsersGroup size={14} />
                                            </div>
                                            <span
                                                className="font-bold text-slate-800 truncate"
                                                title={group.name}
                                            >
                                                {group.name}
                                            </span>
                                            <ShowPaxGroup
                                                size="xs"
                                                adults={draft.adults}
                                                children={draft.children}
                                                seniors={draft.seniors}
                                                infants={draft.infants}
                                                guests={draft.guests}
                                                showRetired={Boolean(booking.service_data?.show_retired)}
                                            />
                                        </div>

                                        <div className="flex items-center gap-1 shrink-0">
                                            {isLinked && (
                                                <Tooltip label={t('match_booking_capacities')} withArrow>
                                                    <ActionIcon
                                                        color="indigo"
                                                        variant="subtle"
                                                        size="sm"
                                                        radius="md"
                                                        disabled={isLoadingThis || (actioningGroupId !== null)}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMatchBookingCapacity(group);
                                                        }}
                                                    >
                                                        <IconArrowsExchange size={13} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            )}
                                            {isLinked && linkedGroup?.is_custom_config && (
                                                <Tooltip label={t('reset_to_group_defaults')} withArrow>
                                                    <ActionIcon
                                                        color="gray"
                                                        variant="subtle"
                                                        size="sm"
                                                        radius="md"
                                                        disabled={isLoadingThis || (actioningGroupId !== null)}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleAssignGroup(group, true);
                                                        }}
                                                    >
                                                        <IconRotateClockwise size={13} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            )}
                                            {isLinked ? (
                                                <ActionIcon
                                                    color="red"
                                                    variant="light"
                                                    size="sm"
                                                    radius="md"
                                                    loading={isLoadingThis}
                                                    disabled={actioningGroupId !== null}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        gid && handleRemoveGroup(gid);
                                                    }}
                                                    title={t('remove_from_service')}
                                                >
                                                    <IconDoorExit size={13} />
                                                </ActionIcon>
                                            ) : (
                                                <ActionIcon
                                                    color="green"
                                                    variant="light"
                                                    size="sm"
                                                    radius="md"
                                                    loading={isLoadingThis}
                                                    disabled={actioningGroupId !== null}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAssignGroup(group);
                                                    }}
                                                    title={t('assign_to_service')}
                                                >
                                                    <IconDoorEnter size={13} />
                                                </ActionIcon>
                                            )}
                                        </div>
                                    </div>

                                    {/* Editable Group Sizes when Linked */}
                                    {isLinked && (
                                        <div className={`
                                            p-2 rounded-md
                                            ${isLinked && linkedGroup?.is_custom_config ? 'bg-amber-200' : 'bg-green-200'}
                                        `}>
                                            <div className="grid grid-cols-5 gap-1.5">
                                                {SIZE_KEYS.map((key) => {
                                                    const labelKey = key === 'seniors' && booking.service_data?.show_retired ? 'retired_plural' : key;
                                                    return (
                                                    <Tooltip key={key} label={t(labelKey)} position="top" withArrow openDelay={300}>
                                                        <div>
                                                            <NumberInput
                                                                size="xs"
                                                                min={0}
                                                                hideControls
                                                                leftSection={<PaxCategoryBadge category={labelKey} />}
                                                                leftSectionWidth={24}
                                                                value={draft[key] || ''}
                                                                onChange={(val) =>
                                                                    handleGroupSizeChange(group, key, Number(val) || 0)
                                                                }
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        saveGroupDraft(group, draft);
                                                                    }
                                                                }}
                                                                className="font-semibold"
                                                                styles={{
                                                                    input: {
                                                                        paddingLeft: 24,
                                                                        paddingRight: 4,
                                                                        textAlign: 'center',
                                                                        height: 26,
                                                                        minHeight: 26,
                                                                        fontSize: 11,
                                                                    },
                                                                }}
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Search and Select All / None when Group has Passengers */}
                                    {groupPersons.length > 0 && (
                                        <div className="space-y-1.5">
                                            {(groupPersons.length > 2 || isLinked) && (
                                                <div className="flex items-center justify-between gap-1.5">
                                                    {groupPersons.length > 4 ? (
                                                        <TextInput
                                                            size="xs"
                                                            value={groupSearchQueries[gid] || ''}
                                                            onChange={(e) => {
                                                                const val = e.currentTarget.value;
                                                                setGroupSearchQueries((prev) => ({
                                                                    ...prev,
                                                                    [gid]: val,
                                                                }));
                                                            }}
                                                            placeholder={t('search_passengers')}
                                                            leftSection={<IconSearch size={12} className="text-slate-400" />}
                                                            rightSection={
                                                                groupSearchQueries[gid] ? (
                                                                    <ActionIcon
                                                                        size="xs"
                                                                        variant="subtle"
                                                                        color="gray"
                                                                        onClick={() =>
                                                                            setGroupSearchQueries((prev) => ({
                                                                                ...prev,
                                                                                [gid]: '',
                                                                            }))
                                                                        }
                                                                    >
                                                                        <IconX size={10} />
                                                                    </ActionIcon>
                                                                ) : null
                                                            }
                                                            className="flex-1"
                                                        />
                                                    ) : <div />}

                                                    {isLinked && groupPersons.length > 1 && (
                                                        <div className="flex items-center gap-1 shrink-0">
                                                            <Button
                                                                size="compact-xs"
                                                                variant="subtle"
                                                                color="gray"
                                                                disabled={actioningGroupId !== null}
                                                                onClick={() => handleSelectAllPersons(group, true)}
                                                            >
                                                                {t('all')}
                                                            </Button>
                                                            <Button
                                                                size="compact-xs"
                                                                variant="subtle"
                                                                color="gray"
                                                                disabled={actioningGroupId !== null}
                                                                onClick={() => handleSelectAllPersons(group, false)}
                                                            >
                                                                {t('deselect_all')}
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Individual Passengers List inside Group */}
                                            <div className="space-y-0.5 max-h-[280px] overflow-y-auto subtle-scrollbar pr-0.5">
                                                {visibleGroupPersons
                                                    .sort((a, b) => a.name.localeCompare(b.name))
                                                    .map((person) => {
                                                        if (!person.id) return null;
                                                        const pid = Number(person.id);
                                                        const pState = draft.persons[pid] || {
                                                            selected: isLinked,
                                                            ...getPersonDefaultSizes(person),
                                                        };
                                                        const isPersonSelected = Boolean(isLinked && pState.selected);
                                                        const hasNonZeroPersonSizes =
                                                            (pState.adults + pState.children + pState.seniors + pState.infants + pState.guests) > 0;
                                                        const showPersonSizes = Boolean(expandedPersonSizeIds[pid] || hasNonZeroPersonSizes);

                                                        return (
                                                            <div
                                                                key={pid}
                                                                className={`p-1.5 rounded-md transition-colors space-y-1.5 ${
                                                                    isPersonSelected ? 'bg-emerald-50/70' : 'bg-gray-100'
                                                                }`}
                                                            >
                                                                <div className="flex items-center justify-between gap-2">
                                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                        <Checkbox
                                                                            size="xs"
                                                                            color="black"
                                                                            checked={isPersonSelected}
                                                                            disabled={actioningGroupId !== null}
                                                                            onChange={(e) =>
                                                                                handleTogglePerson(
                                                                                    group,
                                                                                    person,
                                                                                    e.currentTarget.checked,
                                                                                    isLinked
                                                                                )
                                                                            }
                                                                        />
                                                                        <IconUser size={11} className="text-slate-400 shrink-0" />
                                                                        <span
                                                                            className="font-semibold text-xs text-slate-800 truncate"
                                                                            title={person.name}
                                                                        >
                                                                            {person.name}
                                                                        </span>
                                                                        {person.document_number && (
                                                                            <span className="text-[10px] text-slate-400 shrink-0">
                                                                                {person.document_number}
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    <ActionIcon
                                                                        size="xs"
                                                                        variant="subtle"
                                                                        color={showPersonSizes ? 'indigo' : 'gray'}
                                                                        onClick={() =>
                                                                            setExpandedPersonSizeIds((prev) => ({
                                                                                ...prev,
                                                                                [pid]: !prev[pid],
                                                                            }))
                                                                        }
                                                                    >
                                                                        <IconAdjustmentsHorizontal size={11} />
                                                                    </ActionIcon>
                                                                </div>

                                                                {showPersonSizes && (
                                                                    <div className="grid grid-cols-5 gap-1.5 pl-5">
                                                                        {SIZE_KEYS.map((key) => {
                                                                            const labelKey = key === 'seniors' && booking.service_data?.show_retired ? 'retired_plural' : key;
                                                                            return (
                                                                            <Tooltip
                                                                                key={key}
                                                                                label={t(labelKey)}
                                                                                position="top"
                                                                                withArrow
                                                                                openDelay={300}
                                                                            >
                                                                                <div>
                                                                                    <NumberInput
                                                                                        size="xs"
                                                                                        min={0}
                                                                                        hideControls
                                                                                        leftSection={<PaxCategoryBadge category={labelKey} />}
                                                                                        leftSectionWidth={22}
                                                                                        value={pState[key] || ''}
                                                                                        onChange={(val) =>
                                                                                            handlePersonSizeChange(
                                                                                                group,
                                                                                                person,
                                                                                                key,
                                                                                                Number(val) || 0
                                                                                            )
                                                                                        }
                                                                                        onKeyDown={(e) => {
                                                                                            if (e.key === 'Enter') {
                                                                                                e.preventDefault();
                                                                                                saveGroupDraft(group, draft);
                                                                                            }
                                                                                        }}
                                                                                        className="font-semibold"
                                                                                        styles={{
                                                                                            input: {
                                                                                                borderColor: '#eee',
                                                                                                paddingLeft: 22,
                                                                                                paddingRight: 3,
                                                                                                textAlign: 'center',
                                                                                                height: 24,
                                                                                                minHeight: 24,
                                                                                                fontSize: 11,
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            </Tooltip>
                                                                        );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Save / Cancel Bar when Group or Person Sizes are Modified */}
                                    {isLinked && (
                                        <div className="flex items-center justify-end gap-3">
                                            <Button
                                                size="compact-xs"
                                                color="black"
                                                disabled={isLoadingThis || !isDirty}
                                                onClick={() => saveGroupDraft(group, draft)}
                                            >
                                                {t('save')}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            }
        </div>
    );
}
